import authService from './auth.service';
import type { PostEndpointConfig, PrefillEndpointConfig } from '../types/action-admin.types';

const API_URL = '/api/v1';

class ActionsService {
  async listGroups(): Promise<{ code: string; name: string }[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/actions/groups`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить группы действий');
    }
    const data = await res.json().catch(() => []);
    if (!Array.isArray(data)) return [];
    return data
      .map((g: any) => {
        if (typeof g === 'string') return { code: g, name: g };
        const code = g?.code ?? (g?.id != null ? String(g.id) : g?.value ?? '');
        const name = g?.name ?? g?.title ?? g?.label ?? code;
        return code ? { code, name } : null;
      })
      .filter(Boolean) as { code: string; name: string }[];
  }
  async addHistory(
    action: { id?: number; code?: string } | string,
    payload: { subject_type: string; subject_id: number | null; comment: string; call_id?: string; direction?: string | null; payload?: unknown; values?: unknown }
  ): Promise<void> {
    // Поддержка action_code (приоритет) и action_id (обратная совместимость)
    const actionCode = typeof action === 'string' ? action : (action as any)?.code;
    const actionId = typeof action === 'object' && !actionCode ? (action as any)?.id : undefined;

    const identifier = actionCode ? encodeURIComponent(String(actionCode)) : String(actionId);

    const res = await authService.fetchWithAuth(`${API_URL}/actions/${identifier}/history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject_type: payload.subject_type,
        subject_id: payload.subject_id,
        comment: payload.comment,
        call_id: payload.call_id ?? '',
        direction: payload.direction ?? '',
        payload: payload.payload ?? undefined,
        values: payload.values ?? undefined,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось записать историю действия');
    }
  }

  async uploadAndAttach(
    action: { id?: number; code?: string } | number,
    payload: { subject_type: string; subject_id: number; comment: string; values?: string; payload?: string; files?: File[]; file_document_types?: (number | null)[] }
  ): Promise<void> {
    const form = new FormData();
    form.append('subject_type', payload.subject_type);
    form.append('subject_id', String(payload.subject_id));
    form.append('comment', payload.comment || '');
    if (payload.payload != null && String(payload.payload).length > 0) {
      form.append('payload', String(payload.payload));
    } else if (payload.values != null && String(payload.values).length > 0) {
      // обратная совместимость, если где-то ещё передают values
      form.append('values', String(payload.values));
    }
    (payload.files || []).forEach((f) => form.append('files', f, f.name));
    if (payload.file_document_types && payload.file_document_types.length > 0) {
      form.append('file_document_types', JSON.stringify(payload.file_document_types));
    }

    // Переход на action_code. Для обратной совместимости поддержим и action_id
    const actionId = typeof action === 'number' ? action : (action as any)?.id;
    const actionCode = typeof action === 'object' ? (action as any)?.code : undefined;
    const query = actionCode
      ? `action_code=${encodeURIComponent(String(actionCode))}`
      : `action_id=${encodeURIComponent(String(actionId))}`;

    const res = await authService.fetchWithAuthForm(`${API_URL}/actions/upload-and-attach?${query}`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось записать историю и вложения');
    }
  }

  async listHistory(params: { subject_type: string; subject_id: number; limit?: number; offset?: number }): Promise<{
    id: number;
    action_code?: string;
    action_name?: string;
    subject_type: string;
    subject_id: number;
    comment: string;
    created_by_user_id: number | null;
    created_by_full_name?: string | null;
    created_at: string;
    values: any[];
    payload?: any;
    system_payload?: Record<string, { label: string; type: string; value: any }> | null;
    attachments?: { key: string; name: string; size: number; last_modified: string }[];
  }[]> {
    const limit = params.limit ?? 100;
    const offset = params.offset ?? 0;
    const query = new URLSearchParams({
      subject_type: params.subject_type,
      subject_id: String(params.subject_id),
      limit: String(limit),
      offset: String(offset),
    }).toString();
    const res = await authService.fetchWithAuth(`${API_URL}/actions/history?${query}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить историю действий');
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }

  /**
   * Серверная пагинация + фильтрация истории действий.
   * Новый метод — старый listHistory() остаётся без изменений для обратной совместимости.
   */
  async listHistoryPaginated(params: {
    subject_type: string;
    subject_id: number;
    page?: number;
    page_size?: number;
    action_code?: string[];
    created_by_user_id?: number;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
  }): Promise<{
    items: {
      id: number;
      action_code?: string;
      action_name?: string;
      subject_type: string;
      subject_id: number;
      comment: string;
      created_by_user_id: number | null;
      created_by_full_name?: string | null;
      created_at: string;
      values: any[];
      payload?: any;
      system_payload?: Record<string, { label: string; type: string; value: any }> | null;
      attachments?: { key: string; name: string; size: number; last_modified: string }[];
    }[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const sp = new URLSearchParams({
      subject_type: params.subject_type,
      subject_id: String(params.subject_id),
    });
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.page_size !== undefined) sp.set('page_size', String(params.page_size));
    if (params.action_code?.length) {
      for (const code of params.action_code) {
        sp.append('action_code', code);
      }
    }
    if (params.created_by_user_id !== undefined) {
      sp.set('created_by_user_id', String(params.created_by_user_id));
    }
    if (params.date_from) sp.set('date_from', params.date_from);
    if (params.date_to) sp.set('date_to', params.date_to);
    if (params.sort_by) sp.set('sort_by', params.sort_by);
    if (params.sort_dir) sp.set('sort_dir', params.sort_dir);

    const res = await authService.fetchWithAuth(`${API_URL}/actions/history?${sp.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить историю действий');
    }
    const data = await res.json();
    // Fallback: если ответ — массив (старый формат), обернуть
    if (Array.isArray(data)) {
      return {
        items: data,
        total: data.length,
        page: params.page ?? 1,
        page_size: params.page_size ?? data.length,
      };
    }
    return data;
  }

  /**
   * Получить динамические опции для select-поля из options_query.
   * Для radio_table поддерживает пагинацию (limit/offset/q).
   * Ответ: массив [{value, label}] или объект {items, total}.
   */
  async getFieldOptions(
    actionCode: string,
    fieldName: string,
    params?: { limit?: number; offset?: number; q?: string }
  ): Promise<any> {
    const usp = new URLSearchParams();
    if (params?.limit !== undefined) usp.set('limit', String(params.limit));
    if (params?.offset !== undefined) usp.set('offset', String(params.offset));
    if (params?.q) usp.set('q', params.q);
    const query = usp.toString();
    const url = `${API_URL}/actions/${encodeURIComponent(actionCode)}/field-options/${encodeURIComponent(fieldName)}${query ? `?${query}` : ''}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить опции поля');
    }
    return res.json();
  }

  /**
   * Вызов эндпоинта для предзаполнения полей формы.
   * URL поддерживает шаблоны: {subject_id}, {subject_type}.
   * Возвращает объект { field_name: value }.
   */
  async callPrefillEndpoint(
    config: PrefillEndpointConfig,
    context: { subject_type: string; subject_id: number }
  ): Promise<Record<string, any>> {
    if (!config.url) {
      throw new Error('prefill_endpoint: отсутствует url');
    }
    const url = config.url
      .replace('{subject_id}', String(context.subject_id))
      .replace('{subject_type}', context.subject_type);

    const method = config.method || 'GET';
    const fetchOptions: RequestInit = { method };
    if (method === 'POST') {
      fetchOptions.headers = { 'Content-Type': 'application/json' };
      fetchOptions.body = JSON.stringify(context);
    }
    const res = await authService.fetchWithAuth(url, fetchOptions);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить данные для заполнения формы');
    }
    return res.json();
  }

  /**
   * Вызов дополнительного эндпоинта перед записью действия в историю.
   * URL и payload_values поддерживают шаблоны: {subject_id}, {subject_type}.
   * При multipart=true отправляет FormData (для файлов).
   */
  async callPostEndpoint(
    config: PostEndpointConfig,
    context: { subject_type: string; subject_id: number },
    formPayload: Record<string, any>,
    files?: File[]
  ): Promise<void> {
    if (!config.url) {
      throw new Error('post_endpoint: отсутствует url');
    }
    const replaceTemplates = (str: string) =>
      str.replace('{subject_id}', String(context.subject_id))
         .replace('{subject_type}', context.subject_type)
         .replace(/\{payload\.(\w+)\}/g, (_, key) => String(formPayload[key] ?? ''));

    const url = replaceTemplates(config.url);

    const body: Record<string, any> = {};
    if (config.include_subject !== false) {
      body.subject_type = context.subject_type;
      body.subject_id = context.subject_id;
    }
    if (config.payload_fields?.length) {
      for (const key of config.payload_fields) {
        if (key in formPayload) body[key] = formPayload[key];
      }
    } else {
      Object.assign(body, formPayload);
    }
    if (config.payload_values) {
      for (const [k, v] of Object.entries(config.payload_values)) {
        body[k] = typeof v === 'string' ? replaceTemplates(v) : v;
      }
    }

    let res: Response;
    if (config.multipart) {
      const form = new FormData();
      for (const [k, v] of Object.entries(body)) {
        if (v !== undefined && v !== null) form.append(k, String(v));
      }
      (files || []).forEach((f) => form.append('attachments', f, f.name));
      res = await authService.fetchWithAuthForm(url, {
        method: config.method || 'POST',
        body: form,
      });
    } else {
      res = await authService.fetchWithAuth(url, {
        method: config.method || 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    }
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Ошибка вызова дополнительного эндпоинта');
    }
  }

  /**
   * Отправка изменения данных (data_change action)
   */
  async submitDataChange(params: {
    subject_type: string;
    subject_id: number;
    comment: string;
    payload: {
      field_name: string;
      field_label: string;
      input_type?: string;
      old_value: any;
      new_value: any;
      new_value_id?: unknown;
      dict_code?: string;
    };
  }): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/actions/data_change/history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject_type: params.subject_type,
        subject_id: params.subject_id,
        comment: params.comment,
        payload: params.payload,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось записать изменение данных');
    }
  }
}

export default new ActionsService();
